import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../../components/SEO';
import { 
  FaCheckCircle, 
  FaWarehouse, 
  FaUsers, 
  FaHeadset, 
  FaPlay, 
  FaArrowRight,
  FaCheck,
  FaDownload,
  FaFileAlt,
  FaChartLine
} from 'react-icons/fa';
import BrandText from '../../styles/brandStyles';

// Default fallback image URL
const FALLBACK_IMAGE = 'https://via.placeholder.com/600x400/003171/FFFFFF?text=OPUS+ERP';

const ErpOperaciones = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [isSticky, setIsSticky] = useState(false);
  
  // Reference for the scroll handler to avoid recreating it on every render
  const scrollHandlerRef = useRef(null);

  // Simplified image error handling
  const handleImageError = (e) => {
    e.target.src = FALLBACK_IMAGE;
    e.target.onerror = null; // Prevent infinite error loops
  };

  // Memoized scroll to section function
  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 140; // Header + submenu height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }, []);

  // More efficient scroll handler with throttling
  useEffect(() => {
    // Reset scroll position when component mounts
    window.scrollTo(0, 0);
    
    // Throttling function to limit the number of scroll events
    const throttle = (func, limit) => {
      let inThrottle;
      return function() {
        const args = arguments;
        const context = this;
        if (!inThrottle) {
          func.apply(context, args);
          inThrottle = true;
          setTimeout(() => inThrottle = false, limit);
        }
      };
    };

    // The actual scroll handler
    const handleScroll = () => {
      // Detect if submenu should be sticky
      const heroSection = document.getElementById('overview');
      const headerHeight = 64;
      
      if (heroSection) {
        const heroBottom = heroSection.getBoundingClientRect().bottom;
        setIsSticky(heroBottom <= headerHeight);
      }

      // Detect which section is visible
      const sections = ['overview', 'features', 'capabilities', 'benefits', 'cta'];
      let activeFound = false;
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          const offset = 140;
          if (rect.top <= offset && rect.bottom >= offset) {
            setActiveSection(section);
            activeFound = true;
            break;
          }
        }
      }
      
      // Keep sticky menu if no section is active
      if (!activeFound && window.scrollY > 0) {
        setIsSticky(true);
      }
    };

    // Create a throttled version of the scroll handler
    scrollHandlerRef.current = throttle(handleScroll, 100);
    
    // Initial call to set state correctly
    handleScroll();
    
    // Add event listener with throttled handler
    window.addEventListener('scroll', scrollHandlerRef.current);
    
    // Cleanup
    return () => {
      if (scrollHandlerRef.current) {
        window.removeEventListener('scroll', scrollHandlerRef.current);
      }
    };
  }, []);

  // Predefine feature boxes content
  const featureBoxes = [
    {
      icon: <FaPlay className="w-5 h-5 text-[#0066CC]" />,
      title: "VIDEOS",
      subtitle: "Canal oficial",
      text: "Consulta el canal oficial y descubre todo lo que OPUS ERP tiene para tí.",
      linkText: "Ver ahora",
      linkIcon: <FaArrowRight className="ml-2" />,
      linkUrl: "/academia-opus"
    },
    {
      icon: <FaFileAlt className="w-5 h-5 text-[#0066CC]" />,
      title: "MÁS INFORMACIÓN",
      subtitle: "Sistema Completo",
      text: "Conoce todos los módulos y funcionalidades que ofrece OPUS ERP para empresas y constructoras.",
      linkText: "Ver sistema completo",
      linkIcon: <FaArrowRight className="ml-2" />,
      linkUrl: "/productos/opus-erp"
    },
    {
      icon: <FaChartLine className="w-5 h-5 text-[#0066CC]" />,
      title: "CONOCE A FONDO",
      subtitle: "Deseo una demostración",
      text: "Agenda una presentación personalizada con nuestros expertos.",
      linkText: "Contáctanos",
      linkIcon: <FaArrowRight className="ml-2" />,
      linkUrl: "/Distribuidores"
    }
  ];

  // Predefine capabilities content
  const capabilities = [
    {
      title: "Adiós a los retrasos y al inventario excesivo",
      description: "Optimiza el control de inventario con gestión en tiempo real, evitando excesos y faltantes, mientras mantienes actualizada la disponibilidad de productos.",
      image: "/images/modules/opus_erp/004 inventario.png",
      features: [
        "Control de inventario en tiempo real.",
        "Gestión eficiente de la cadena de suministro.",
        "Alertas automáticas para niveles mínimos.",
        "Trazabilidad completa de productos."
      ],
      reversed: false
    },
    {
      title: "Contratación, empleados, evaluaciones y flota",
      description: "Automatiza el proceso de compras corporativas, desde solicitudes hasta aprobaciones, controlando presupuestos y optimizando gastos en toda la organización.",
      image: "/images/modules/opus_erp/001 compras.png",
      features: [
        "Optimización del proceso de contratación.",
        "Control exhaustivo de gastos de personal.",
        "Evaluación de desempeño integrada.",
        "Gestión completa de flota vehicular."
      ],
      reversed: true
    },
    {
      title: "Servicio de asistencia",
      description: "Gestiona proyectos completos de servicio al cliente, con seguimiento de tickets, cronogramas de atención y asignación de recursos técnicos.",
      image: "/images/modules/opus_erp/002 proyectos.png",
      features: [
        "Seguimiento eficiente de tickets de clientes.",
        "Priorización automática de problemas.",
        "Resolución oportuna de incidencias.",
        "Análisis de satisfacción de clientes."
      ],
      reversed: false
    }
  ];

  // Benefits data for the benefits section
  const benefits = [
    {
      title: "Mayor eficiencia operativa",
      text: "Optimiza todos los procesos de negocio para reducir costos y aumentar la productividad."
    },
    {
      title: "Control de inventario preciso",
      text: "Evita excesos y faltantes con una gestión inteligente de inventarios."
    },
    {
      title: "Mejor atención al cliente",
      text: "Resuelve incidencias más rápido y aumenta la satisfacción del cliente."
    },
    {
      title: "Decisiones basadas en datos",
      text: "Analiza el rendimiento con reportes detallados y toma mejores decisiones."
    },
    {
      title: "Gestión efectiva de personal",
      text: "Administra mejor tus recursos humanos y aumenta la retención de talento."
    },
    {
      title: "Integración completa",
      text: "Se integra con otros módulos OPUS ERP para un flujo de trabajo unificado."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="OPUS ERP - Operaciones | ECOSOFT"
        description="Optimiza la gestión de inventarios, mantenimiento, compras y logística con el módulo de Operaciones de OPUS ERP."
        keywords="OPUS, ERP, operaciones, inventario, mantenimiento, logística, recursos humanos, servicio"
      />
      
      {/* Hero Section */}
      <section id="overview" className="relative h-[450px] bg-gradient-to-r from-[#003171] to-[#0066CC]">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="absolute inset-0">
          <img 
            src="/images/erpBAN/erp_op.jpg" 
            alt="OPUS ERP Operaciones" 
            className="w-full h-full object-cover"
            onError={handleImageError}
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-4 h-full flex items-center pt-12 relative z-10">
          <div className="max-w-2xl text-white">
            <div className="flex items-center mb-6">
              <div className="relative w-auto h-auto mr-6 flex-shrink-0">
                <div className="bg-white/90 rounded-lg px-8 py-3 pt-4 shadow-lg">
                  <img 
                    src="/images/opus-erp-logo.svg" 
                    alt="OPUS ERP" 
                    className="h-12 w-auto object-contain" 
                    onError={handleImageError}
                  />
                </div>
              </div>
              <div className="flex flex-col justify-center">
                <h1 className="text-5xl font-bold"><BrandText>OPUS ERP</BrandText></h1>
                <h2 className="text-4xl font-bold mt-2">Operaciones</h2>
              </div>
            </div>
            <p className="text-xl mb-8">
              Haz más en menos tiempo. Optimiza la gestión de inventarios, mantenimiento, compras y logística.
            </p>
            <div className="flex gap-4">
              <a href="/documents/opus_e_empresas-1.pdf" download className="bg-white text-[#0066CC] px-8 py-3 rounded-md font-semibold hover:bg-gray-100 transition-colors flex items-center">
                <FaDownload className="mr-2" />
                Folleto Empresas
              </a>
              <a href="/documents/opus_e_constructoras-1.pdf" download className="border-2 border-white text-white px-8 py-3 rounded-md font-semibold hover:bg-white/10 transition-colors flex items-center">
                <FaDownload className="mr-2" />
                Folleto Constructoras
              </a>
            </div>
          </div>
        </div>
      </section>
      
      {/* Submenu Navigation */}
      <div 
        className={`bg-white shadow-md transition-all duration-300 sticky-nav ${
          isSticky 
            ? 'fixed top-[64px] left-0 right-0 z-30 border-t border-gray-200' 
            : 'relative'
        }`}
        style={{ minHeight: '56px' }}
      >
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex overflow-x-auto py-4 items-center relative">
            <div className="flex-grow flex">
              {/* Map over sections for more efficient code */}
              {['overview', 'features', 'capabilities', 'benefits', 'cta'].map(section => (
                <button 
                  key={section}
                  onClick={() => scrollToSection(section)}
                  className={`px-6 py-2 whitespace-nowrap font-medium ${
                    activeSection === section 
                      ? 'text-[#0066CC] border-b-2 border-[#0066CC]' 
                      : 'text-gray-600 hover:text-[#0066CC]'
                  }`}
                >
                  {section === 'overview' ? 'Resumen' : 
                   section === 'features' ? 'Características' :
                   section === 'capabilities' ? 'Capacidades' :
                   section === 'benefits' ? 'Beneficios' : 'Comenzar'}
                </button>
              ))}
            </div>
            <div className="flex items-center ml-4">
              <div className="bg-white/90 rounded-lg px-4 py-2 shadow-sm">
                <img 
                  src="/images/opus-erp-logo.svg" 
                  alt="OPUS ERP - Operaciones" 
                  className="h-8 w-auto object-contain"
                  onError={handleImageError}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Features Grid */}
      <section id="features" className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Map over feature boxes for cleaner code */}
            {featureBoxes.map((box, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg flex flex-col h-full">
                <div className="w-10 h-10 bg-[#0066CC]/10 rounded-lg flex items-center justify-center mb-4">
                  {box.icon}
                </div>
                <h3 className="text-lg font-semibold mb-1">{box.title}</h3>
                <h4 className="text-base font-medium mb-2">{box.subtitle}</h4>
                <p className="text-gray-600 mb-4 flex-grow text-sm">
                  {box.text}
                </p>
                <Link to={box.linkUrl} className="text-[#0066CC] font-medium inline-flex items-center text-sm">
                  {box.linkText} {box.linkIcon}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Capabilities Section */}
      <section id="capabilities" className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-3">Lo que puedes hacer con OPUS ERP Operaciones</h2>
          <p className="text-xl text-gray-600 text-center mb-10 max-w-3xl mx-auto">
            Descubre cómo OPUS ERP Operaciones te ayuda a optimizar la gestión de inventarios, recursos humanos y servicio al cliente.
          </p>
          
          <div className="space-y-12">
            {/* Map over capabilities for more efficient code */}
            {capabilities.map((capability, index) => (
              <div 
                key={index} 
                className={`flex flex-col ${capability.reversed ? 'md:flex-row-reverse' : 'md:flex-row'} gap-6 items-center`}
              >
                <div className="w-full md:w-1/2">
                  {/* Use proper background fallback and error handling */}
                  <div className="relative rounded-lg overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-[#003171] to-[#0066CC]"></div>
                    <img 
                      src={capability.image} 
                      alt={capability.title} 
                      className="relative z-10 rounded-lg shadow-lg w-full h-auto object-cover"
                      onError={handleImageError}
                    />
                  </div>
                </div>
                <div className="w-full md:w-1/2">
                  <h3 className="text-2xl font-bold mb-3">{capability.title}</h3>
                  <p className="text-gray-600 mb-3">
                    {capability.description}
                  </p>
                  <ul className="space-y-2 mb-4">
                    {capability.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <FaCheck className="text-[#0066CC] mt-1 mr-2 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Key Benefits */}
      <section id="benefits" className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8">Beneficios clave</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Split benefits into two columns for better rendering */}
            <div className="space-y-4">
              {benefits.slice(0, 3).map((benefit, index) => (
                <div key={index} className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-[#0066CC] flex items-center justify-center mt-1 mr-4">
                    <FaCheck className="text-white text-sm" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-1">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-4">
              {benefits.slice(3).map((benefit, index) => (
                <div key={index} className="flex items-start">
                  <div className="w-6 h-6 rounded-full bg-[#0066CC] flex items-center justify-center mt-1 mr-4">
                    <FaCheck className="text-white text-sm" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-1">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Integration with other modules */}
      <section className="py-16 bg-[#F5F7FA]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8 text-[#1A2B39]">Automatiza todos los procesos de tu empresa con OPUS ERP</h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Descubre los diferentes módulos de OPUS ERP y cómo pueden ayudarte a optimizar tu negocio
          </p>
          
          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="bg-[#F5F7FA] p-5 rounded-lg text-center border-2 border-[#0066CC] hover:bg-[#0066CC]/5 transition-colors">
              <h3 className="text-lg font-bold mb-2 text-[#1A2B39]">Operaciones</h3>
              <p className="text-gray-600 text-sm">Haz más en menos tiempo.</p>
              <div className="mt-3 h-1 w-full bg-[#0066CC] rounded"></div>
            </div>
            
            <Link to="/erp/ventas" className="bg-[#F5F7FA] p-5 rounded-lg text-center hover:bg-[#0066CC]/5 transition-colors">
              <h3 className="text-lg font-bold mb-2 text-[#1A2B39]">Ventas</h3>
              <p className="text-gray-600 text-sm">De la propuesta a la firma electrónica.</p>
              <div className="mt-3 h-1 w-full bg-[#0066CC] rounded"></div>
            </Link>
            
            <Link to="/erp/productividad" className="bg-[#F5F7FA] p-5 rounded-lg text-center hover:bg-[#0066CC]/5 transition-colors">
              <h3 className="text-lg font-bold mb-2 text-[#1A2B39]">Productividad</h3>
              <p className="text-gray-600 text-sm">Los hechos tan rápido como el pensamiento.</p>
              <div className="mt-3 h-1 w-full bg-[#0066CC] rounded"></div>
            </Link>
            
            <Link to="/erp/web" className="bg-[#F5F7FA] p-5 rounded-lg text-center hover:bg-[#0066CC]/5 transition-colors">
              <h3 className="text-lg font-bold mb-2 text-[#1A2B39]">Web</h3>
              <p className="text-gray-600 text-sm">Creación de webs para tu empresa.</p>
              <div className="mt-3 h-1 w-full bg-[#0066CC] rounded"></div>
            </Link>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section id="cta" className="py-16 bg-[#1A2B39] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">¿Listo para optimizar tus operaciones empresariales?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Descubre cómo OPUS ERP Operaciones puede transformar la eficiencia de tu empresa con 
            herramientas avanzadas de gestión.
          </p>
          <div className="flex flex-wrap justify-center">
            <Link 
              to="/Distribuidores"
              className="px-10 py-4 bg-[#0066CC] hover:bg-[#0088FF] text-white text-lg font-medium rounded-md transition-colors"
            >
              Solicitar Demostración
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ErpOperaciones; 